import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import type { PromiseToPay } from '@/types/collections';

interface NextActionPanelProps {
  promise: PromiseToPay;
  onCompleteAction: () => void;
}

export const NextActionPanel: React.FC<NextActionPanelProps> = ({ promise, onCompleteAction }) => {
  // Determine the next action based on promise status and date
  const getNextAction = () => {
    const today = new Date();
    const promiseDate = new Date(promise.promiseDate);
    const daysUntilPromise = Math.ceil((promiseDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    switch (promise.status) {
      case 'Active':
        if (daysUntilPromise < 0) {
          // Promise date has passed
          return {
            title: 'Promise Date Passed - Verify Payment',
            description: 'The promise date has passed. Check if payment was received and update the promise status.',
            icon: <AlertCircle className="h-5 w-5" />,
            urgency: 'high' as const,
            actionType: 'verify_payment' as const,
            daysInfo: `${Math.abs(daysUntilPromise)} day${Math.abs(daysUntilPromise) !== 1 ? 's' : ''} overdue`,
          };
        } else if (daysUntilPromise === 0) {
          // Promise date is today
          return {
            title: 'Promise Due Today - Monitor Payment',
            description: 'Payment is expected today. Monitor for incoming payment and follow up if needed.',
            icon: <Clock className="h-5 w-5" />,
            urgency: 'medium' as const,
            actionType: 'monitor_payment' as const,
            daysInfo: 'Due today',
          };
        } else if (daysUntilPromise <= 3) {
          // Promise date is within 3 days
          return {
            title: 'Send Payment Reminder',
            description: 'Promise date is approaching. Send a friendly reminder to the customer.',
            icon: <Mail className="h-5 w-5" />,
            urgency: 'medium' as const,
            actionType: 'send_reminder' as const,
            daysInfo: `Due in ${daysUntilPromise} day${daysUntilPromise !== 1 ? 's' : ''}`,
          };
        } else if (daysUntilPromise <= 7) {
          // Promise date is within a week
          return {
            title: 'Schedule Follow-up Call',
            description: 'Promise date is coming up. Schedule a follow-up call to confirm commitment.',
            icon: <Phone className="h-5 w-5" />,
            urgency: 'low' as const,
            actionType: 'schedule_followup' as const,
            daysInfo: `Due in ${daysUntilPromise} days`,
          };
        } else {
          // Promise date is more than a week away
          return {
            title: 'Monitor Promise Status',
            description: 'Continue monitoring this promise. No immediate action required.',
            icon: <TrendingUp className="h-5 w-5" />,
            urgency: 'low' as const,
            actionType: 'monitor' as const,
            daysInfo: `Due in ${daysUntilPromise} days`,
          };
        }

      case 'Broken':
        return {
          title: 'Promise Broken - Take Action',
          description: 'This promise has been broken. Contact the customer to understand the situation and negotiate a new arrangement.',
          icon: <AlertCircle className="h-5 w-5" />,
          urgency: 'high' as const,
          actionType: 'handle_broken' as const,
          daysInfo: promise.daysOverdue ? `${promise.daysOverdue} day${promise.daysOverdue !== 1 ? 's' : ''} overdue` : 'Broken',
        };

      case 'Partially Paid':
        return {
          title: 'Partial Payment Received',
          description: 'Customer has made a partial payment. Follow up to collect the remaining balance.',
          icon: <DollarSign className="h-5 w-5" />,
          urgency: 'medium' as const,
          actionType: 'collect_balance' as const,
          daysInfo: 'Partially paid',
        };

      case 'Paid':
        return {
          title: 'Promise Fulfilled',
          description: 'Payment has been received in full. No further action required.',
          icon: <CheckCircle2 className="h-5 w-5" />,
          urgency: 'none' as const,
          actionType: 'completed' as const,
          daysInfo: 'Completed',
        };

      default:
        return {
          title: 'Review Promise',
          description: 'Review this promise and determine the next appropriate action.',
          icon: <Calendar className="h-5 w-5" />,
          urgency: 'low' as const,
          actionType: 'review' as const,
          daysInfo: 'Pending review',
        };
    }
  };

  const nextAction = getNextAction();

  // Get urgency styling
  const getUrgencyStyles = () => {
    switch (nextAction.urgency) {
      case 'high':
        return {
          border: 'border-l-4 border-l-red-500',
          iconBg: 'bg-red-100 dark:bg-red-900/30',
          iconColor: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
          badgeText: 'Urgent',
        };
      case 'medium':
        return {
          border: 'border-l-4 border-l-amber-500',
          iconBg: 'bg-amber-100 dark:bg-amber-900/30',
          iconColor: 'text-amber-600 dark:text-amber-400',
          badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
          badgeText: 'Action Required',
        };
      case 'low':
        return {
          border: 'border-l-4 border-l-blue-500',
          iconBg: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: 'text-blue-600 dark:text-blue-400',
          badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
          badgeText: 'Routine',
        };
      case 'none':
        return {
          border: 'border-l-4 border-l-green-500',
          iconBg: 'bg-green-100 dark:bg-green-900/30',
          iconColor: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
          badgeText: 'Completed',
        };
      default:
        return {
          border: 'border-l-4 border-l-gray-500',
          iconBg: 'bg-gray-100 dark:bg-gray-900/30',
          iconColor: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
          badgeText: 'Review',
        };
    }
  };

  const styles = getUrgencyStyles();

  return (
    <Card className={`${styles.border} shadow-sm hover:shadow-md transition-shadow`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`${styles.iconBg} ${styles.iconColor} p-2.5 rounded-lg shrink-0`}>
              {nextAction.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-lg leading-tight">{nextAction.title}</CardTitle>
              </div>
              <CardDescription className="text-sm leading-relaxed">
                {nextAction.description}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge className={styles.badge}>{styles.badgeText}</Badge>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{nextAction.daysInfo}</span>
            </div>
          </div>
          {nextAction.urgency !== 'none' && (
            <Button onClick={onCompleteAction} className="gap-2 shrink-0">
              Complete Action
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
